/**
 * Pure view-mappers for the Clinical-Edit rail (PROD-1262).
 *
 * Faithful to Flutter `clinical_edit_tab.dart` where it has behavior (split the
 * comma-separated `suggestion` into chips), plus the PROD-1041 improvements the
 * Flutter source lacks: severity → Banner variant (Flutter uses one red flag for
 * everything) and severity-sorted ordering.
 */
import type { ClinicalEditData } from '@/types/clinical-edit-api';

export type ClinicalEditVariant = 'error' | 'warning' | 'info';

/**
 * Map the backend `type` to a Banner variant. The live value-set isn't branched
 * in the Flutter source, so this is defensive + case-insensitive; unknown/blank
 * → "warning" (a clinical flag, but not over-alarming — matches PROD-1041 D4,
 * which treats even hard errors as warn-only in V1). Confirm against live data.
 */
const ERROR_TYPES = new Set(['error', 'hard', 'critical', 'high', 'block', 'blocking']);
const INFO_TYPES = new Set(['info', 'information', 'informational', 'low', 'suggestion', 'note']);
const WARNING_TYPES = new Set(['warning', 'warn', 'soft', 'medium']);
/** Warn once per unrecognised `type` in dev so the live value-set is traceable
 *  (the backend enum isn't confirmed yet — see the PROD-1262 follow-up). */
const warnedTypes = new Set<string>();

export function clinicalEditVariant(type: string | null | undefined): ClinicalEditVariant {
  const t = (type ?? '').trim().toLowerCase();
  if (ERROR_TYPES.has(t)) return 'error';
  if (INFO_TYPES.has(t)) return 'info';
  if (t && !WARNING_TYPES.has(t) && import.meta.env.DEV && !warnedTypes.has(t)) {
    warnedTypes.add(t);
    console.warn(`[clinical-edit] unmapped edit type "${type}" → defaulting to "warning". Confirm the backend value-set (PROD-1262).`);
  }
  // 'warning' | 'warn' | 'soft' | 'medium' | unknown | '' → warning
  return 'warning';
}

/** Order so the most severe edits surface first (error → warning → info). */
const SEVERITY_RANK: Record<ClinicalEditVariant, number> = { error: 0, warning: 1, info: 2 };

/** Split the comma-separated `suggestion` into trimmed, non-empty codes (Flutter parity). */
export function splitSuggestions(suggestion: string | null | undefined): string[] {
  if (!suggestion) return [];
  return suggestion
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export interface ClinicalEditView {
  variant: ClinicalEditVariant;
  title: string;
  subtitle?: string;
  description?: string;
  suggestedCodes: string[];
}

export function toClinicalEditView(data: ClinicalEditData): ClinicalEditView {
  return {
    variant: clinicalEditVariant(data.type),
    title: (data.title ?? '').trim() || 'Clinical edit',
    subtitle: data.subtitle?.trim() || undefined,
    description: data.description?.trim() || undefined,
    suggestedCodes: splitSuggestions(data.suggestion),
  };
}

/** Map + severity-sort a list of clinical edits for display. Stable within a severity. */
export function toClinicalEditViews(items: ClinicalEditData[]): ClinicalEditView[] {
  return items
    .map(toClinicalEditView)
    .map((v, i) => ({ v, i }))
    .sort((a, b) => SEVERITY_RANK[a.v.variant] - SEVERITY_RANK[b.v.variant] || a.i - b.i)
    .map(({ v }) => v);
}
